import { useCallback, useMemo, useRef, useState } from "react";
import { Game, INTERNATIONAL, BRAZILIAN, type Move, type VariantSpec } from "@fmjd/engine";
import { Board, moveNotation } from "../components/Board.js";

/**
 * A local demo board.
 *
 * Runs the real engine in the browser with no server involved, so it shows
 * that the rules are correct — maximum capture, flying kings, backward
 * capture by men, and the FMJD draw rules — before any of the tournament
 * machinery exists.
 */
export function PlayDemo() {
  const [variantId, setVariantId] = useState<"international" | "brazilian">("international");
  const variant: VariantSpec = variantId === "international" ? INTERNATIONAL : BRAZILIAN;

  // The Game object is mutable, so a counter drives re-rendering rather than
  // cloning the whole game on every move.
  const gameRef = useRef(new Game(variant));
  const [, bump] = useState(0);
  const rerender = () => bump((n) => n + 1);

  const [selected, setSelected] = useState<number | null>(null);
  /**
   * Landing squares clicked so far in a multi-jump capture, excluding the
   * origin.
   *
   * A capture is entered hop by hop rather than as from/to. Two different
   * sequences can share the same from and to — most obviously a circular
   * capture that returns to its origin — and they remove different pieces, so
   * from/to alone cannot identify the move. The player must be able to say
   * which way round they went.
   */
  const [partialPath, setPartialPath] = useState<number[]>([]);
  const [flipped, setFlipped] = useState(false);
  const [showNumbers, setShowNumbers] = useState(true);
  const [history, setHistory] = useState<string[]>([]);

  const game = gameRef.current;
  const legal = game.legalMoves();
  const outcome = game.outcome();

  const reset = useCallback((v: VariantSpec) => {
    gameRef.current = new Game(v);
    setSelected(null);
    setPartialPath([]);
    setHistory([]);
    rerender();
  }, []);

  /** Squares a piece may move from — only those with a legal move. */
  const origins = useMemo(() => new Set(legal.map((m) => m.from)), [legal]);

  /** Moves still possible given the origin and the hops entered so far. */
  const matching = useMemo(() => {
    if (selected === null) return [];
    return legal.filter(
      (m) =>
        m.from === selected &&
        partialPath.every((sq, i) => m.path[i] === sq) &&
        m.path.length >= partialPath.length
    );
  }, [legal, selected, partialPath]);

  /**
   * The squares that may be clicked next.
   *
   * With a partial capture entered, only the next hop of a still-matching
   * sequence is offered — never a later one, which would skip over a choice
   * the player has to make.
   */
  const targets = useMemo(() => {
    const next = new Set<number>();
    for (const m of matching) {
      const sq = m.path[partialPath.length];
      if (sq !== undefined) next.add(sq);
    }
    return [...next];
  }, [matching, partialPath]);

  const lastMove: Move | null = game.history.length
    ? (game.history[game.history.length - 1] ?? null)
    : null;

  /** Squares to light up as part of the capture being entered. */
  const pathHighlight = useMemo(
    () => (selected === null ? [] : [selected, ...partialPath]),
    [selected, partialPath]
  );

  function play(move: Move) {
    game.move(move.from, move.to, move.path);
    setHistory((h) => [...h, moveNotation(move)]);
    setSelected(null);
    setPartialPath([]);
    rerender();
  }

  function onSquareClick(square: number) {
    if (outcome) return;

    // Nothing selected: pick up a piece that has a legal move.
    if (selected === null) {
      if (origins.has(square)) setSelected(square);
      return;
    }

    // Clicking the origin again cancels — unless it is a legal next hop,
    // which is exactly the circular-capture case where a piece returns to the
    // square it started from. The move wins over the cancel.
    if (!targets.includes(square)) {
      if (square === selected) {
        setSelected(null);
        setPartialPath([]);
      } else {
        setSelected(origins.has(square) ? square : null);
        setPartialPath([]);
      }
      return;
    }

    const path = [...partialPath, square];
    const still = matching.filter((m) => path.every((sq, i) => m.path[i] === sq));

    // A sequence is complete only when the path is fully entered. A shorter
    // move that is a prefix of a longer one must not fire early.
    const complete = still.find((m) => m.path.length === path.length);
    const longer = still.filter((m) => m.path.length > path.length);

    if (complete && longer.length === 0) {
      play(complete);
      return;
    }

    setPartialPath(path);
  }

  const captureForced = legal.length > 0 && legal[0]!.captures.length > 0;
  const capturing = partialPath.length > 0;

  return (
    <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div>
        <Board
          variant={variant}
          board={game.board}
          flipped={flipped}
          selected={selected}
          targets={targets}
          pathSquares={pathHighlight}
          lastMove={lastMove}
          onSquareClick={onSquareClick}
          showNumbers={showNumbers}
        />
      </div>

      <div style={{ minWidth: 260, flex: 1 }}>
        <h2 style={{ marginTop: 0 }}>Demo board</h2>
        <p style={{ color: "#666", fontSize: 14, marginTop: -8 }}>
          The real rules engine, running in your browser. No server involved.
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#555" }}>
            Variant
          </label>
          <select
            value={variantId}
            onChange={(e) => {
              const id = e.target.value as "international" | "brazilian";
              setVariantId(id);
              reset(id === "international" ? INTERNATIONAL : BRAZILIAN);
            }}
            style={{ padding: "6px 10px", fontSize: 14 }}
          >
            <option value="international">International — 10×10</option>
            <option value="brazilian">Brazilian — 8×8</option>
          </select>
        </div>

        <div
          style={{
            padding: "10px 12px",
            background: outcome ? "#e8f5e9" : capturing ? "#fff8e1" : "#f5f5f5",
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          {outcome ? (
            <>
              <strong>
                {outcome.result === "draw"
                  ? "Draw"
                  : outcome.result === "white"
                    ? "White wins"
                    : "Black wins"}
              </strong>
              <div style={{ fontSize: 13, color: "#555" }}>
                {describeReason(outcome.reason)}
              </div>
            </>
          ) : capturing ? (
            <>
              <strong>Capturing…</strong>
              <div style={{ fontSize: 13, color: "#555" }}>
                {selected}
                {partialPath.map((sq) => `×${sq}`).join("")} — click the next square
                {targets.length > 1 && ` (${targets.length} ways to continue)`}
              </div>
            </>
          ) : (
            <>
              <strong>{game.turn === "w" ? "White" : "Black"} to move</strong>
              <div style={{ fontSize: 13, color: "#555" }}>
                {legal.length} legal move{legal.length === 1 ? "" : "s"}
                {captureForced && " — capture is compulsory"}
              </div>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button onClick={() => reset(variant)} style={btn}>
            New game
          </button>
          <button onClick={() => setFlipped((f) => !f)} style={btn}>
            Flip board
          </button>
          <button onClick={() => setShowNumbers((s) => !s)} style={btn}>
            {showNumbers ? "Hide" : "Show"} numbers
          </button>
          {capturing && (
            <button
              onClick={() => {
                setSelected(null);
                setPartialPath([]);
              }}
              style={{ ...btn, borderColor: "#c62828", color: "#c62828" }}
            >
              Cancel capture
            </button>
          )}
        </div>

        <h3 style={{ fontSize: 14, marginBottom: 6 }}>Moves</h3>
        <div
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 13,
            maxHeight: 240,
            overflowY: "auto",
            background: "#fafafa",
            padding: 10,
            borderRadius: 6,
            border: "1px solid #eee",
          }}
        >
          {history.length === 0 ? (
            <span style={{ color: "#999" }}>No moves yet</span>
          ) : (
            history.map((h, i) => (
              <span key={i} style={{ marginRight: 10 }}>
                {i % 2 === 0 && <span style={{ color: "#999" }}>{i / 2 + 1}. </span>}
                {h}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function describeReason(reason: string): string {
  switch (reason) {
    case "blocked":
      return "No legal move available (FMJD 7.2.3)";
    case "no-pieces":
      return "No pieces remaining (FMJD 7.2.2)";
    case "repetition":
      return "Same position three times (FMJD 6.1)";
    case "no-progress":
      return "25 king moves without a capture (FMJD 6.2)";
    case "endgame-3v1":
      return "Three against one, 16 moves (FMJD 6.3)";
    case "endgame-2v1":
      return "Two against one, 5 moves (FMJD 6.4)";
    default:
      return reason;
  }
}

const btn: React.CSSProperties = {
  padding: "7px 14px",
  fontSize: 14,
  border: "1px solid #ccc",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
};
